/**
 * Storedash Clear Filters Handler
 *
 * Manages the clear filters button functionality:
 * - Removes all active filter parameters from URL
 * - Updates page via History API without reload
 * - Integrates with the Storedash filter system
 * - Shows/hides button based on active filters
 *
 * @package Storedash
 * @since 3.1.0
 */

(function() {
    'use strict';

    class ClearFiltersHandler {
        constructor() {
            this.wrapper = null;
            this.button = null;
            this.hideWhenNoFilters = true;
            this.abortController = null;
        }

        /**
         * Initialize the clear filters handler
         */
        init() {
            this.wrapper = document.querySelector('.storedash-clear-filters-wrapper');

            if (!this.wrapper) {
                return;
            }

            this.button = this.wrapper.querySelector('.storedash-clear-filters-button');
            this.hideWhenNoFilters = this.wrapper.getAttribute('data-hide-when-no-filters') === 'yes';

            // Set initial visibility
            this.checkFilterStatus();

            // Bind button click event
            if (this.button) {
                this.button.addEventListener('click', (e) => this.handleClearClick(e));
            }

            // Listen for filter changes from other widgets and update visibility
            document.addEventListener('storedash:filters:changed', () => {
                this.checkFilterStatus();
            });

            // Also listen for URL changes via History API
            window.addEventListener('popstate', () => {
                this.checkFilterStatus();
            });
        }

        /**
         * Check if any filters are currently active
         * @returns {boolean} True if filters are active
         */
        hasActiveFilters() {
            const params = new URLSearchParams(window.location.search);
            const filterParams = Array.from(params.keys()).filter(key => key.startsWith('filter_'));
            return filterParams.length > 0;
        }

        /**
         * Check and update button visibility based on active filters
         */
        checkFilterStatus() {
            if (!this.hideWhenNoFilters) {
                // Always show if not set to hide
                this.wrapper.classList.remove('storedash-hidden-no-filters');
                this.wrapper.classList.add('storedash-has-active-filters');
                return;
            }

            const hasFilters = this.hasActiveFilters();

            if (hasFilters) {
                this.wrapper.classList.remove('storedash-hidden-no-filters');
                this.wrapper.classList.add('storedash-has-active-filters');
            } else {
                this.wrapper.classList.add('storedash-hidden-no-filters');
                this.wrapper.classList.remove('storedash-has-active-filters');
            }
        }

        /**
         * Build context information (mirrors filters.js getContext)
         * Reads server-injected context from data-page-context attribute (authoritative).
         * Falls back to body class sniffing for backward compatibility.
         * @returns {Object}
         */
        getContext() {
            // Priority 1: Server-injected context (authoritative, works on all Elementor templates)
            const wrapper = document.querySelector('.storedash-products-wrapper[data-page-context]');
            if (wrapper) {
                try {
                    const ctx = JSON.parse(wrapper.dataset.pageContext);
                    return {
                        is_shop: ctx.is_shop || false,
                        is_category: ctx.is_category || false,
                        is_taxonomy: ctx.is_taxonomy || false,
                        is_search: ctx.is_search || false,
                        category_id: ctx.category_id || 0,
                        taxonomy: ctx.taxonomy || '',
                        term_id: ctx.term_id || 0,
                        search: ctx.search || '',
                    };
                } catch (e) { /* fall through to body class sniffing */ }
            }

            // Priority 2: Body class sniffing (legacy fallback)
            const body = document.body;
            const params = new URLSearchParams(window.location.search);
            const context = {
                is_shop: body.classList.contains('post-type-archive-product') || body.classList.contains('woocommerce-shop'),
                is_category: body.classList.contains('tax-product_cat'),
                is_taxonomy: body.classList.contains('tax-product_tag') || body.classList.contains('woocommerce-taxonomy'),
                is_search: body.classList.contains('search-results') || params.has('s'),
                category_id: 0,
                taxonomy: '',
                term_id: 0,
                search: params.get('s') || '',
            };

            if (context.is_category) {
                const classes = body.className.split(' ');
                for (const className of classes) {
                    if (className.startsWith('term-')) {
                        const termId = className.replace('term-', '');
                        if (!isNaN(termId)) {
                            context.category_id = parseInt(termId);
                            context.term_id = parseInt(termId);
                            context.taxonomy = 'product_cat';
                            break;
                        }
                    }
                }
            }

            if (context.is_taxonomy && !context.is_category) {
                const classes = body.className.split(' ');
                for (const className of classes) {
                    if (className.startsWith('term-')) {
                        const termId = className.replace('term-', '');
                        if (!isNaN(termId)) {
                            context.term_id = parseInt(termId);
                        }
                    }
                    if (className.startsWith('tax-')) {
                        context.taxonomy = className.replace('tax-', '');
                    }
                }
            }

            return context;
        }

        /**
         * Read product grid widget settings (mirrors filters.js/getWidgetSettings)
         * @returns {Object}
         */
        getWidgetSettings() {
            const productGridWidget = document.querySelector('.elementor-widget-storedash-product-grid');
            const productsWrapper = productGridWidget ?
                productGridWidget.querySelector('.storedash-products-wrapper[data-settings]') :
                document.querySelector('.storedash-products-wrapper[data-settings]');

            if (!productsWrapper) {
                return {};
            }

            const settingsData = productsWrapper.dataset.settings;
            if (settingsData) {
                try {
                    return JSON.parse(settingsData);
                } catch (error) {
                    console.warn('Storedash: Failed to parse widget settings', error.message);
                }
            }

            return {};
        }

        /**
         * Handle clear filters button click
         */
        handleClearClick(e) {
            e.preventDefault();

            // BULLETPROOF FIX: Cancel all pending filter requests before clearing
            if (window.StoredashRequestManager) {
                window.StoredashRequestManager.cancelAll();
            }

            // Clear all pending filter debounces
            document.querySelectorAll('.storedash-filter').forEach(filter => {
                const instance = filter._storedashFilterInstance;
                if (instance) {
                    clearTimeout(instance.debounceTimer);
                    if (instance.controller) {
                        instance.controller.abort();
                    }
                    instance.isFiltering = false;
                }
            });

            // Invalidate in-flight product grid updates
            const grids = document.querySelectorAll('.elementor-widget-storedash-product-grid');
            grids.forEach(grid => {
                const controller = grid._storedashProductGridController;
                if (controller) {
                    controller.lastRequestId++; // Invalidate pending requests
                    controller.isUpdating = false;
                }
            });

            // Get current URL parameters
            const params = new URLSearchParams(window.location.search);
            const filterKeysToRemove = Array.from(params.keys()).filter(key => key.startsWith('filter_'));

            // Remove all filter parameters
            filterKeysToRemove.forEach(key => {
                params.delete(key);
            });

            // Also remove pagination when clearing filters
            params.delete('paged');
            params.delete('page');

            // Build new URL
            const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');

            // Update URL via History API
            window.history.pushState(
                {
                    filters: {},
                    timestamp: Date.now(),
                    storedashFilters: true,
                    cleared: true,
                    source: 'clear-filters-button'
                },
                '',
                newUrl
            );

            // Show skeleton loaders while fetching (mirrors filters.js loading behaviour)
            document.dispatchEvent(new CustomEvent('storedash:filters:loading', { bubbles: true }));

            // Fetch unfiltered products via AJAX (NO page reload!)
            this.fetchUnfilteredProducts();

            // Update button visibility
            this.checkFilterStatus();

            // Scroll to products (optional - smooth UX)
            this.scrollToProducts();
        }

        /**
         * Fetch all products (no filters) via AJAX
         * Uses the same endpoint as the Filters widget
         */
        async fetchUnfilteredProducts() {
            if (this.abortController) {
                this.abortController.abort();
            }
            this.abortController = new AbortController();

            try {

                // Check if Storedash filter data is available
                if (typeof storedashFiltersData === 'undefined') {
                    // Dispatch minimal event to trigger Product Grid update
                    this.dispatchMinimalEvent();
                    return;
                }

                const context = this.getContext();
                const widgetSettings = this.getWidgetSettings();

                // FIX: Read current sort from URL to preserve user's sort selection
                // When user sorts by "Price: High to Low", we want to keep that after clearing filters
                const urlParams = new URLSearchParams(window.location.search);
                const currentOrderby = urlParams.get('orderby');
                const currentOrder = urlParams.get('order');

                // Priority: URL params > widget settings > global defaults
                // URL params represent user's active sort choice
                // Widget settings represent Elementor widget configuration
                // Global defaults are the fallback
                const orderby = currentOrderby || widgetSettings.orderby || storedashFiltersData.orderby || 'menu_order';
                const order = currentOrder || widgetSettings.order || storedashFiltersData.order || 'ASC';

                // Safety net: If widget is in archive_mode but perPage shows default 12,
                // prefer the global storedashFiltersData.perPage which reflects WooCommerce settings
                let perPage = widgetSettings.products_per_page;
                if (widgetSettings.archive_mode === 'yes' && (!perPage || perPage === 12)) {
                    perPage = storedashFiltersData.perPage;
                }
                perPage = perPage || storedashFiltersData.perPage || 12;

                const response = await fetch(storedashFiltersData.restUrl + 'storedash/v1/ajax-filter', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-WP-Nonce': storedashFiltersData.nonce || '',
                    },
                    body: JSON.stringify({
                        filters: {}, // Empty filters = all products
                        context: context,
                        paged: 1,
                        per_page: perPage,
                        orderby: orderby,
                        order: order,
                        widget_settings: widgetSettings,
                        render: 'html',
                    }),
                    signal: this.abortController.signal
                });

                if (!response.ok) {
                    this.dispatchMinimalEvent();
                    return;
                }

                const data = await response.json();

                // Handle both wrapped and unwrapped response formats
                const responseData = data.data || data;

            if (!responseData || !responseData.product_ids) {
                    this.dispatchMinimalEvent();
                    return;
                }

                // Trigger event with proper data structure for Product Grid
                const event = new CustomEvent('storedash:filters:changed', {
                    detail: {
                        filters: {},
                        productIds: responseData.product_ids || [],
                        products_html: responseData.products_html || '',
                        products: responseData.products || [],
                        filter_counts: responseData.filter_counts || {},
                        widget_settings: responseData.widget_settings || widgetSettings,
                        i18n: responseData.i18n || {},
                        render_mode: responseData.render_mode || 'html',
                        totalProducts: responseData.total_products || 0,
                        totalPages: responseData.total_pages || 1,
                        currentPage: responseData.current_page || 1,
                        perPage: responseData.per_page || 12,
                        cleared: true,
                        source: 'clear-filters-button'
                    },
                    bubbles: true,
                    cancelable: true
                });

                document.dispatchEvent(event);

            } catch (error) {
                if (error.name === 'AbortError') return;
                console.warn('Storedash: Clear filters request failed', error.message);
                this.dispatchMinimalEvent();
            }
        }

        /**
         * Dispatch a minimal event for Product Grid to handle
         * Triggers an update without relying on AJAX response
         */
        dispatchMinimalEvent() {
            const event = new CustomEvent('storedash:filters:changed', {
                detail: {
                    filters: {},
                    cleared: true,
                    context: this.getContext(),
                    widget_settings: this.getWidgetSettings(),
                    products: [],
                    i18n: {},
                    source: 'clear-filters-button'
                },
                bubbles: true,
                cancelable: true
            });
            document.dispatchEvent(event);
        }

        /**
         * Scroll to product grid after clearing filters
         * Looks for common product grid selectors
         */
        scrollToProducts() {
            // Try to find product grid
            const selectors = [
                '.storedash-product-grid',
                '.woocommerce-product-list',
                '.products',
                '[data-display-type="grid"]'
            ];

            for (const selector of selectors) {
                const grid = document.querySelector(selector);
                if (grid) {
                    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                    grid.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
                    return;
                }
            }

            // Fallback: scroll to top
            const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
        }
    }

    // Initialize when DOM is ready
    function initClearFiltersHandler() {
        const handler = new ClearFiltersHandler();
        handler.init();
        return handler;
    }

    let handlerInstance = null;

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            handlerInstance = initClearFiltersHandler();

            // Fallback: Try again after a small delay if widget wasn't found
            if (!handlerInstance.wrapper) {
                setTimeout(() => {
                    handlerInstance = initClearFiltersHandler();
                }, 100);
            }
        });
    } else {
        handlerInstance = initClearFiltersHandler();

        // Fallback: Try again after a small delay if widget wasn't found
        if (!handlerInstance.wrapper) {
            setTimeout(() => {
                handlerInstance = initClearFiltersHandler();
            }, 100);
        }
    }

    // Also re-initialize when Elementor editor updates
    if (window.elementorFrontend && window.elementorFrontend.hooks) {
        window.elementorFrontend.hooks.addAction('frontend/element_ready/storedash-clear-filters.default', () => {
            handlerInstance = initClearFiltersHandler();
        });
    }
})();
